import { ROUTES } from "../routes/routes.constants";
import { transformModulesToMenu } from "./menuTransformer";
import { SIDEBAR_RAIL } from "../layout/sidebarRail.config";
/**
 * Checks if a given pathname is authorized based on user permissions and modules.
 *
 * @param pathname - The current URL pathname
 * @param modules - The raw modules array from Redux/API
 * @param isSuperAdmin - Whether the user is a super admin
 * @returns boolean - True if authorized, false otherwise
 */
export function isPathAuthorized(pathname, modules = [], isSuperAdmin = false, permissions = {}, entityType = "") {
    // 1. Super Admin always has access to everything
    if (isSuperAdmin)
        return true;
    // 2. Whitelist: Routes that are always accessible to any authenticated user
    const whitelistedPaths = [
        ROUTES.root.path,
        ROUTES.home.path,
        ROUTES.favorites.path,
        ROUTES.settings.path,
        ROUTES.profile.path,
        ROUTES.notFound.path,
        ROUTES.publisherReporting.path,
        ROUTES.accountOverview.path,
        ROUTES.addOutreachTracker.path,
        // Deep-linkable from the Ad Testing result email. The page enforces its own
        // module permission (hasAccess) + server-side 403s, so the route is safe to allow
        // here without the global layout blocking a legitimate link (mirrors accountOverview).
        ROUTES.adTesting.path,
    ];
    // Dynamic segment routes — check by prefix or exact match
    if (pathname.startsWith('/outreach-tracker/'))
        return true;
    if (pathname === '/affiliate-tracker/client-approval' || pathname.startsWith('/affiliate-tracker/client-approval'))
        return true;
    if (pathname === '/social-yt-tracker' || pathname.startsWith('/social-yt-tracker/'))
        return true;
    // Meeting Scorer action-email page: an AM opens their own meeting without needing
    // Meeting Scorer access (the server allows only that meeting's AMs).
    if (pathname.startsWith('/meeting-scorer/action/'))
        return true;
    // Check against whitelist
    if (whitelistedPaths.includes(pathname))
        return true;
    // Check against SIDEBAR_RAIL (Home, Favorites, Settings are already in whitelist but just in case)
    if (SIDEBAR_RAIL.some(rail => rail.to === pathname))
        return true;
    // 3. Special handling for complex routes (reports, edit/create pages)
    const isMetaAuditRoute = (path) => {
        const reportRouteBase = ROUTES.metaAdAuditDetail.path.replace('/:reportId', '');
        return path === ROUTES.metaAdAudit.path || path.startsWith(reportRouteBase + '/');
    };
    const isRolesPermissionRoute = (path) => {
        const editRouteBase = ROUTES.rolePermissionEdit.path.replace('/:id', '');
        return path === ROUTES.rolesPermission.path ||
            path.startsWith(ROUTES.rolePermissionCreate.path) ||
            path.startsWith(editRouteBase + '/');
    };
    // Add / Edit / View all hang off the Create Report module.
    // "Manage Reports" action permission is required to access Add and Edit pages.
    // View page and main listing remain accessible to all users with module access.
    const isCreateReportManageRoute = (path) => {
        const editBase = ROUTES.createReportEdit.path.replace('/:reportId', '');
        return path === ROUTES.createReportAdd.path || path.startsWith(editBase + '/');
    };
    const isCreateReportViewRoute = (path) => {
        const viewBase = ROUTES.createReportView.path.replace('/:reportId', '');
        return path.startsWith(viewBase + '/');
    };
    const canManageReports = entityType !== "client" && Boolean(permissions?.create_report?.actions?.includes("Manage Reports"));
    // 4. Check against Dynamic Menu (Authorized Modules)
    const dynamicMenu = transformModulesToMenu(modules);
    const checkItem = (item) => {
        // Direct match
        if (item.to === pathname)
            return true;
        // Special route matches
        if (item.to === ROUTES.metaAdAudit.path && isMetaAuditRoute(pathname))
            return true;
        if (item.to === ROUTES.rolesPermission.path && isRolesPermissionRoute(pathname))
            return true;
        if (item.to === ROUTES.accountPerformance.path && pathname.startsWith(ROUTES.accountPerformanceReport.path))
            return true;
        if (item.to === ROUTES.commissionTracker.path && pathname.startsWith(ROUTES.commissionTrackerReport.path))
            return true;
        if (item.to === ROUTES.employeePerformance.path && pathname.startsWith(ROUTES.employeePerformanceDetails.path))
            return true;
        if (item.to === ROUTES.createReport.path) {
            if (isCreateReportManageRoute(pathname))
                return canManageReports;
            if (isCreateReportViewRoute(pathname))
                return true;
        }
        // Recursive match for sub-items
        if (item.items && item.items.length > 0) {
            return item.items.some(checkItem);
        }
        return false;
    };
    return dynamicMenu.some(checkItem);
}
